// src/components/OrderDetails/Admin/MobileOrderDetailsView.js
import React from 'react';
import {
  Stack,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationCity as LocationCityIcon,
  LocationOn as LocationOnIcon,
  Notes as NotesIcon,
  ShoppingBag as ShoppingBagIcon
} from '@mui/icons-material';

const MobileOrderDetailsView = ({ 
  order, 
  updating, 
  updateOrderStatus, 
  getStatusDetails, 
  shippingAddress, 
  subtotal, 
  shippingPrice,
  getProductImage,
  getProductName,
  getProductColor
}) => {
  const statusDetails = getStatusDetails(order.status);
  
  return (
    <Stack spacing={2}>
      {/* Order Status with Admin Controls */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">حالة الطلب</Typography>
            <Chip
              icon={statusDetails.icon}
              label={statusDetails.label}
              color={statusDetails.color}
              size="small"
              variant="outlined"
              sx={{
                minWidth: { xs: 100, sm: 120 }, // Responsive minimum width
                maxWidth: 160, // Prevent it from getting too wide
                '& .MuiChip-label': {
                  px: 1,
                  fontSize: '0.75rem',
                },
                '& .MuiChip-icon': {
                  fontSize: '1rem',
                }
              }}
            />
          </Box>
          
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>تغيير الحالة</InputLabel>
            <Select
              value={order.status}
              label="تغيير الحالة"
              onChange={(e) => updateOrderStatus(e.target.value)}
              disabled={updating}
            >
              <MenuItem value="pending">قيد الانتظار</MenuItem>
              <MenuItem value="confirmed">تم التأكيد</MenuItem>
              <MenuItem value="shipped">تم الشحن</MenuItem>
              <MenuItem value="delivered">تم التسليم</MenuItem>
              <MenuItem value="cancelled">ملغى</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">تاريخ الطلب:</Typography>
            <Typography variant="body2" fontWeight="bold">
              {new Date(order.created_at).toLocaleDateString('ar-EG')}
            </Typography>
          </Box>

          {order.updated_at && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" fontWeight="bold">آخر تحديث:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {new Date(order.updated_at).toLocaleDateString('ar-EG')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Products Table - Mobile Version */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingBagIcon color="primary" />
            <Typography fontWeight="600">
              المنتجات ({order.order_items?.length || 0})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ px: 2, pb: 2 }}>
            <TableContainer 
              component={Paper}
              sx={{ 
                direction: 'rtl',
                mb: 2,
                boxShadow: 1,
                borderRadius: 2,
                '& .MuiTable-root': {
                  direction: 'rtl',
                  borderCollapse: 'separate',
                  borderSpacing: 0
                }
              }}
            >
              <Table sx={{ direction: 'rtl' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '12px 8px',
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: 'primary.main',
                      borderTopRightRadius: '8px',
                      fontSize: '0.8rem'
                    }}>
                      المنتج
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '12px 8px',
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: 'primary.main',
                      borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                      fontSize: '0.8rem'
                    }}>
                      الوصف
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '12px 8px',
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: 'primary.main',
                      borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                      fontSize: '0.8rem'
                    }}>
                      السعر
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '12px 8px',
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: 'primary.main',
                      borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                      fontSize: '0.8rem'
                    }}>
                      الكمية
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '12px 8px',
                      fontWeight: 'bold',
                      color: 'white',
                      backgroundColor: 'primary.main',
                      borderTopLeftRadius: '8px',
                      borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                      fontSize: '0.8rem'
                    }}>
                      الإجمالي
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.order_items?.map((item, idx) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{
                        textAlign: 'center',
                        direction: 'rtl',
                        padding: '8px 4px',
                        fontSize: '0.75rem'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Box
                            component="img"
                            src={getProductImage(item) || '/placeholder.png'}
                            alt={getProductName(item)}
                            sx={{ 
                              width: 40, 
                              height: 40,
                              objectFit: 'cover', 
                              borderRadius: 1
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        textAlign: 'center',
                        direction: 'rtl',
                        padding: '8px 4px',
                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                        fontSize: '0.75rem',
                        maxWidth: '120px'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 600, 
                          mb: 0.5,
                          fontSize: '0.7rem',
                          lineHeight: 1.2
                        }}>
                          {getProductName(item)}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 0.5, 
                          flexWrap: 'wrap',
                          justifyContent: 'center'
                        }}>
                          <Chip 
                            label={`${getProductColor(item)}`} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ 
                              fontWeight: 'bold',
                              fontSize: '0.6rem',
                              height: 18,
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                          />
                          {item.size && (
                            <Chip 
                              label={item.size} 
                              size="small" 
                              color="secondary" 
                              variant="outlined"
                              sx={{ 
                                fontWeight: 'bold',
                                fontSize: '0.6rem',
                                height: 18,
                                '& .MuiChip-label': { px: 0.5 }
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{
                        textAlign: 'center',
                        direction: 'rtl',
                        padding: '8px 4px',
                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                        fontSize: '0.75rem'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}>
                          {item.price_at_purchase.toFixed(2)} ج.م
                        </Typography>
                      </TableCell>
                      <TableCell sx={{
                        textAlign: 'center',
                        direction: 'rtl',
                        padding: '8px 4px',
                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                        fontSize: '0.75rem'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        }}>
                          {item.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{
                        textAlign: 'center',
                        direction: 'rtl',
                        padding: '8px 4px',
                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                        fontSize: '0.75rem'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 700,
                          fontSize: '0.7rem'
                        }}>
                          {(item.quantity * item.price_at_purchase).toFixed(2)} ج.م
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pricing breakdown moved directly under the table */}
            <Card sx={{ borderRadius: 2, boxShadow: 1, backgroundColor: 'grey.50' }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="bold">المجموع الفرعي للمنتجات:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {subtotal.toFixed(2)} ج.م
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="bold">مصاريف الشحن:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="info.main">
                    {shippingPrice.toFixed(2)} ج.م
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="bold">المجموع الكلي:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {order.total_amount.toFixed(2)} ج.م
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Shipping Information - Now includes email */}
      {shippingAddress && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon color="primary" />
              <Typography fontWeight="600">معلومات التوصيل</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {shippingAddress.fullName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">الاسم الكامل</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {shippingAddress.fullName}
                    </Typography>
                  </Box>
                </Box>
              )}
              {shippingAddress.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">رقم الهاتف</Typography>
                    <Typography variant="body2" fontWeight="500" dir="ltr" sx={{ textAlign: 'right' }}>
                      {shippingAddress.phone}
                    </Typography>
                  </Box>
                </Box>
              )}
              {order.profiles?.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">البريد الإلكتروني</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {order.profiles.email}
                    </Typography>
                  </Box>
                </Box>
              )}
              {shippingAddress.governorate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationCityIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">المحافظة</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {shippingAddress.governorate}
                    </Typography>
                  </Box>
                </Box>
              )}
              {shippingAddress.address && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">العنوان</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {shippingAddress.address}
                    </Typography>
                  </Box>
                </Box>
              )}
              {shippingPrice > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalShippingIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">تكلفة الشحن</Typography>
                    <Typography variant="body2" fontWeight="500" color="info.main">
                      {shippingPrice.toFixed(2)} ج.م
                    </Typography>
                  </Box>
                </Box>
              )}
              {shippingAddress.notes && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <NotesIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.5 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">ملاحظات إضافية</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {shippingAddress.notes}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Payment Method and Screenshot */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon color="primary" />
            <Typography fontWeight="600">طريقة الدفع</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
              {order.payment_method === 'vodafone_cash'
                ? 'فودافون كاش'
                : order.payment_method === 'cash_on_delivery'
                  ? 'الدفع عند الاستلام'
                  : order.payment_method}
            </Typography>
            
            {/* Payment Screenshot */}
            {order.payment_screenshot && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                  إيصال الدفع:
                </Typography>
                <Box sx={{ 
                  border: '2px dashed', 
                  borderColor: 'divider', 
                  borderRadius: 2, 
                  p: 1, 
                  textAlign: 'center' 
                }}>
                  <img
                    src={`${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/payment-screenshots/${order.user_id}/${order.payment_screenshot}`}
                    alt="Payment screenshot"
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 200, 
                      borderRadius: 8 
                    }}
                    onError={(e) => {
                      e.target.src = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/payment-screenshots/${order.payment_screenshot}`;
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default MobileOrderDetailsView;